import * as React from 'react';

import {TrialGatheringPreparing} from 'shells/trial-gathering/trial-gathering-preparing';

import {
    getMobileUdid,
    isMobileSignup,
    loginWithAuthToken,
    mobileFailAlert,
    trackAuthError,
} from '../../utils';

const MOBILE_SIGNUP_FAILED =
    'We created your account, but could not sign you in. Please try logging in.';

type Props = {
    pills: string[];
    isProvisioningComplete: boolean;
    errorProvisioning: boolean;
    email: string;
    signupToken: string;
    formValues: any;
    instanceId: string;
    emailSha1: string;
};

export function TrialGatheringPreparingPage(props: Props) {
    const [hasCompletedLogin, setHasCompletedLogin] = React.useState<boolean>(false);
    const [progress, setProgress] = React.useState<number>(0.1);

    React.useEffect(() => {
        const interval = setInterval(() => {
            setProgress((prevProgress) => {
                const newProgress = prevProgress + 0.02;

                return newProgress > 0.95 ? 0.95 : newProgress;
            });
        }, 100);

        return () => clearInterval(interval);
    }, []);

    React.useEffect(() => {
        if (props.isProvisioningComplete && progress >= 0.9 && !hasCompletedLogin) {
            setHasCompletedLogin(true);

            // trigger analytics event (which go on to google tag manager (gtm), segment, etc)
            Analytics.trialSignupComplete(
                props.email,
                props.formValues,
                props.instanceId,
                props.emailSha1
            );

            const isMobile = isMobileSignup();

            // Both paths redeem the signup token here. A browser gets a session
            // back and moves on to the app; a mobile client gets a device token
            // to authenticate its own API calls with, because the signup token
            // itself is not something the mobile auth strategy can resolve.
            fetch('/auth/signup-login', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    type: 7 /* User_Token::TYPE_INITIAL_LOGIN */,
                    token: props.signupToken,
                    username: props.email,
                    signupFormValues: props.formValues,
                    udid: isMobile ? getMobileUdid() : undefined,
                }),
            })
                .then((response) => response.json())
                .then((json) => {
                    if (isMobile) {
                        if (json.authToken) {
                            loginWithAuthToken(props.email, json.authToken);
                        } else {
                            trackAuthError(
                                `Signup login returned no device token: ${JSON.stringify(json)}`
                            );
                            mobileFailAlert(MOBILE_SIGNUP_FAILED);
                        }

                        return;
                    }

                    if (json.success === true) {
                        // wait 500 ms to give GTM + Segment time to send the event
                        setTimeout(() => {
                            // @ts-expect-error
                            window.location = '/explore';
                        }, 500);
                    }
                })
                .catch((error) => {
                    // this is the last step of signup — failing it silently
                    // leaves the user on the loading screen indefinitely
                    trackAuthError('Could not complete signup login', error);

                    if (isMobile) {
                        mobileFailAlert(MOBILE_SIGNUP_FAILED);
                    }
                });
        }
    }, [
        props.isProvisioningComplete,
        progress,
        props.email,
        props.signupToken,
        props.formValues,
        props.instanceId,
        props.emailSha1,
        hasCompletedLogin,
    ]);

    return (
        <TrialGatheringPreparing
            pills={props.pills}
            progress={progress}
            errorProvisioning={props.errorProvisioning}
        />
    );
}
